import { apiGet } from '../api';

/** Dashboard data returned from /api/hub/dashboard */
interface DashboardData {
  user: { label: string; role: string; slug: string };
  system: { total_apps: number; healthy_apps: number; version: string };
  apps: Array<{ app_id: string; status: string; port?: number }>;
  hub_url: string;
  connection_state: string;
}

/** Render the mobile dashboard — hub-core experience on phone. */
export function renderHome(container: HTMLElement): void {
  container.innerHTML = `
    <div class="dash" id="mobile-dash">
      <div class="dash-skeleton">
        <div class="skeleton-card" style="min-height:80px"><div class="skeleton-line"></div><div class="skeleton-line"></div></div>
        <div class="dash-stats-skeleton">
          <div class="skeleton-card" style="min-height:64px"><div class="skeleton-line"></div></div>
          <div class="skeleton-card" style="min-height:64px"><div class="skeleton-line"></div></div>
          <div class="skeleton-card" style="min-height:64px"><div class="skeleton-line"></div></div>
        </div>
        <div class="skeleton-card" style="min-height:120px"><div class="skeleton-line"></div><div class="skeleton-line"></div><div class="skeleton-line"></div></div>
      </div>
    </div>
  `;
  void loadDashboard(container);
}

async function loadDashboard(container: HTMLElement): Promise<void> {
  const dash = container.querySelector<HTMLElement>('#mobile-dash');
  if (!dash) return;

  try {
    const data = await apiGet<DashboardData>('/api/hub/dashboard');
    renderDashboardContent(dash, data);
  } catch {
    // Fallback: try loading just the app list
    try {
      const apps = await apiGet<Array<{ id: string; name: string; status: string; port?: number }>>(
        '/api/discover',
      );
      renderFallbackDeck(dash, apps);
    } catch {
      dash.innerHTML = `
        <div class="empty-state">
          <svg class="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"/>
          </svg>
          <span class="empty-state-text">Could not reach your hub. Check your connection.</span>
        </div>`;
    }
  }
}

function renderDashboardContent(dash: HTMLElement, data: DashboardData): void {
  const { user, system, apps } = data;
  const initials = (user.label || 'H').charAt(0).toUpperCase();
  const greeting = getGreeting();
  const running = apps.filter((a) => a.status === 'running');
  const stopped = apps.filter((a) => a.status !== 'running');

  dash.innerHTML = `
    <!-- Welcome banner -->
    <div class="dash-welcome">
      <div class="dash-welcome-row">
        <div class="dash-avatar">${esc(initials)}</div>
        <div class="dash-welcome-text">
          <div class="dash-greeting">${greeting}${user.label ? ', ' + esc(user.label) : ''}</div>
          <div class="dash-role">
            <span class="dash-role-tag">${esc(user.role || 'user')}</span>
            ${user.slug ? `<span class="dash-slug">/h/${esc(user.slug)}</span>` : ''}
          </div>
        </div>
      </div>
    </div>

    <!-- Stats row -->
    <div class="dash-stats">
      <div class="dash-stat-card">
        <div class="dash-stat-value" style="color:var(--text-success)">${system.healthy_apps}</div>
        <div class="dash-stat-label">Running</div>
      </div>
      <div class="dash-stat-card">
        <div class="dash-stat-value">${system.total_apps}</div>
        <div class="dash-stat-label">Total</div>
      </div>
      <div class="dash-stat-card">
        <div class="dash-stat-value" style="font-size:14px">${esc(system.version || '?')}</div>
        <div class="dash-stat-label">Version</div>
      </div>
    </div>

    <!-- Quick actions -->
    <div class="dash-section">
      <div class="dash-section-title">Quick Actions</div>
      <div class="dash-actions">
        <button class="dash-action-btn" data-action="open-people">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
          <span>People</span>
        </button>
        <button class="dash-action-btn" data-action="open-cell">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
          <span>Phone</span>
        </button>
        <button class="dash-action-btn" data-action="open-money">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
          <span>Money</span>
        </button>
        <button class="dash-action-btn" data-action="open-docs">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          <span>Docs</span>
        </button>
      </div>
    </div>

    <!-- Running apps -->
    <div class="dash-section">
      <div class="dash-section-title">Apps <span class="dash-count">${running.length} running</span></div>
      <div class="dash-app-list" id="dash-app-list">
        ${running
          .map(
            (a) => `
          <div class="dash-app-row" data-app="${esc(a.app_id)}">
            <span class="dash-dot dot-green"></span>
            <span class="dash-app-name">${esc(appLabel(a.app_id))}</span>
            <span class="dash-app-status">running</span>
            <button class="dash-open-btn" data-app="${esc(a.app_id)}">Open</button>
          </div>`,
          )
          .join('')}
        ${stopped
          .map(
            (a) => `
          <div class="dash-app-row" data-app="${esc(a.app_id)}">
            <span class="dash-dot dot-red"></span>
            <span class="dash-app-name">${esc(appLabel(a.app_id))}</span>
            <span class="dash-app-status dash-app-stopped">stopped</span>
          </div>`,
          )
          .join('')}
      </div>
    </div>

    <div class="dash-footer">
      <span>The Hub Ecosystems</span>
    </div>
  `;

  // Wire up action buttons
  dash.querySelectorAll<HTMLButtonElement>('.dash-action-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const action = btn.dataset['action'] ?? '';
      const appId = action.replace('open-', 'hub-');
      openApp(appId);
    });
  });

  dash.querySelectorAll<HTMLButtonElement>('.dash-open-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const appId = btn.dataset['app'] ?? '';
      openApp(appId);
    });
  });
}

function renderFallbackDeck(
  dash: HTMLElement,
  apps: Array<{ id: string; name: string; status: string }>,
): void {
  if (apps.length === 0) {
    dash.innerHTML = `
      <div class="empty-state">
        <svg class="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2z"/>
        </svg>
        <span class="empty-state-text">No apps discovered on this hub</span>
      </div>`;
    return;
  }

  dash.innerHTML = `
    <div class="dash-section">
      <div class="dash-section-title">Your Apps</div>
      <div class="dash-app-list">
        ${apps
          .map(
            (a) => `
          <div class="dash-app-row" data-app="${esc(a.id)}">
            <span class="dash-dot ${a.status === 'running' ? 'dot-green' : 'dot-red'}"></span>
            <span class="dash-app-name">${esc(a.name || a.id)}</span>
            <span class="dash-app-status">${esc(a.status)}</span>
            ${a.status === 'running' ? `<button class="dash-open-btn" data-app="${esc(a.id)}">Open</button>` : ''}
          </div>`,
          )
          .join('')}
      </div>
    </div>`;

  dash.querySelectorAll<HTMLButtonElement>('.dash-open-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      openApp(btn.dataset['app'] ?? '');
    });
  });
}

function openApp(appId: string): void {
  // Open app through the proxy in a new tab/webview
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
  const prefix = appId.replace('hub-', '');
  window.open(`/api/proxy/${prefix}/${tokenParam}`, '_blank');
}

function appLabel(appId: string): string {
  const labels: Record<string, string> = {
    'hub-core': 'Core',
    'hub-cell': 'Phone',
    'hub-people': 'People',
    'hub-money': 'Money',
    'hub-docs': 'Docs',
    'hub-mobile': 'Mobile',
    'hub-agent': 'Agent',
  };
  return labels[appId] ?? appId.replace('hub-', '');
}

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
